import { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Typography,
  Chip,
  IconButton,
  Grid,
  Card,
  CardContent,
  CardActions,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CloneIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { agentService } from '../services/agentService';
import { useNotificationStore } from '../store/notificationStore';
import { useContextStore } from '../store/contextStore';
import type { AgentDefinitionDto, AgentFilters } from '../types/agent';
import { TextField } from '../@core/components/mui';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { PageListLayout } from '../components/layouts/PageListLayout';

/**
 * Agent Registry page component.
 */
export const AgentRegistryPage = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AgentDefinitionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AgentFilters>({
    pageNumber: 1,
    pageSize: 12,
    searchTerm: '',
    category: '',
    isSystem: undefined,
    isActive: true,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const { addNotification } = useNotificationStore();
  const currentProject = useContextStore((s) => s.currentProject);
  const currentTenant = useContextStore((s) => s.currentTenant);
  const canCreateAgent = Boolean(currentTenant && currentProject);

  const loadAgents = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await agentService.getAgents(filters);
      setAgents(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load agents');
      addNotification(err.response?.data?.message || 'Failed to load agents', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgents();
  }, [filters.pageNumber, filters.searchTerm, filters.category, filters.isSystem, filters.isActive, currentTenant?.id, currentProject?.id]);

  const handleCreate = () => {
    navigate('/agents/new');
  };

  const handleView = (agent: AgentDefinitionDto) => {
    navigate(`/agents/${agent.id}`);
  };

  const handleEdit = (agent: AgentDefinitionDto) => {
    if (agent.isSystem) {
      addNotification('System agents cannot be edited', 'warning');
      return;
    }
    navigate(`/agents/${agent.id}/edit`);
  };

  const handleDelete = async (agent: AgentDefinitionDto) => {
    if (agent.isSystem) {
      addNotification('System agents cannot be deleted', 'warning');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${agent.name}"?`)) {
      return;
    }

    try {
      await agentService.deleteAgent(agent.id);
      addNotification('Agent deleted successfully', 'success');
      loadAgents();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete agent', 'error');
    }
  };

  const handleClone = async (agent: AgentDefinitionDto) => {
    try {
      const cloned = await agentService.cloneAgent(agent.id);
      addNotification('Agent cloned successfully', 'success');
      navigate(`/agents/${cloned.id}`);
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to clone agent', 'error');
    }
  };

  const categories = [
    'Infrastructure Management',
    'Security & Compliance',
    'Financial Operations',
    'User Support',
    'Monitoring & Observability',
    'Automation & Orchestration',
  ];

  // Define table columns for list view
  const tableColumns: DataTableColumn<AgentDefinitionDto>[] = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 200,
      render: (agent) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {agent.iconName && <Box sx={{ fontSize: '1.25rem' }}>{agent.iconName}</Box>}
          <Typography variant="body2" fontWeight={500}>
            {agent.name}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'description',
      label: 'Description',
      minWidth: 300,
      render: (agent) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {agent.description || 'No description available'}
        </Typography>
      ),
    },
    {
      id: 'category',
      label: 'Category',
      minWidth: 180,
      render: (agent) =>
        agent.category ? (
          <Chip label={agent.category} size="small" variant="outlined" />
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
    {
      id: 'type',
      label: 'Type',
      minWidth: 100,
      render: (agent) => (
        <Chip
          label={agent.isSystem ? 'System' : 'Custom'}
          size="small"
          color={agent.isSystem ? 'primary' : 'default'}
        />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      render: (agent) => (
        <Chip
          label={agent.isActive ? 'Active' : 'Inactive'}
          size="small"
          color={agent.isActive ? 'success' : 'error'}
        />
      ),
    },
    {
      id: 'version',
      label: 'Version',
      minWidth: 80,
      render: (agent) => (
        <Typography variant="body2" color="text.secondary">
          v{agent.version}
        </Typography>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 150,
      align: 'right',
      render: (agent) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => handleView(agent)}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!agent.isSystem && (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => handleEdit(agent)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Clone">
            <IconButton size="small" onClick={() => handleClone(agent)}>
              <CloneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!agent.isSystem && (
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => handleDelete(agent)} color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <PageListLayout
      title="Agent Registry"
      createButton={
        canCreateAgent
          ? { label: 'Create Agent', onClick: handleCreate, icon: <AddIcon /> }
          : undefined
      }
      viewMode="both"
      activeView={viewMode}
      onViewChange={(v) => setViewMode(v)}
      filters={
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <TextField
            label="Search agents"
            size="small"
            value={filters.searchTerm || ''}
            onChange={(e) => {
              setFilters({ ...filters, searchTerm: e.target.value, pageNumber: 1 });
            }}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <TextField
            select
            label="Category"
            size="small"
            value={filters.category || ''}
            onChange={(e) => {
              setFilters({ ...filters, category: e.target.value || undefined, pageNumber: 1 });
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All Categories</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Type"
            size="small"
            value={filters.isSystem === undefined ? '' : filters.isSystem ? 'system' : 'custom'}
            onChange={(e) => {
              const value = e.target.value as string;
              setFilters({
                ...filters,
                isSystem: value === '' ? undefined : value === 'system',
                pageNumber: 1,
              });
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="system">System</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </TextField>
          <TextField
            select
            label="Status"
            size="small"
            value={filters.isActive === undefined ? '' : filters.isActive ? 'active' : 'inactive'}
            onChange={(e) => {
              const value = e.target.value as string;
              setFilters({
                ...filters,
                isActive: value === '' ? undefined : value === 'active',
                pageNumber: 1,
              });
            }}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </Box>
      }
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      isEmpty={agents.length === 0}
      emptyState={{
        title: 'No agents found',
        description: 'Try adjusting your filters or create a new agent to get started.',
        action: { label: 'Create Agent', onClick: handleCreate },
      }}
      pagination={{
        page: filters.pageNumber || 1,
        totalPages: Math.ceil(totalCount / (filters.pageSize || 12)),
        onChange: (page) => setFilters({ ...filters, pageNumber: page }),
      }}
    >
      {!canCreateAgent && currentTenant && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a project (using the project switcher above) to create an agent.
        </Alert>
      )}
      {viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {agents.map((agent) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={agent.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {agent.iconName && (
                      <Box sx={{ mr: 1, fontSize: '1.5rem' }}>{agent.iconName}</Box>
                    )}
                    <Typography variant="h6" component="h2" noWrap>
                      {agent.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                    {agent.description || 'No description available'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    {agent.category && (
                      <Chip label={agent.category} size="small" variant="outlined" />
                    )}
                    <Chip
                      label={agent.isSystem ? 'System' : 'Custom'}
                      size="small"
                      color={agent.isSystem ? 'primary' : 'default'}
                    />
                    <Chip
                      label={agent.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={agent.isActive ? 'success' : 'error'}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Version: {agent.version}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => handleView(agent)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {!agent.isSystem && (
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(agent)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Clone">
                    <IconButton size="small" onClick={() => handleClone(agent)}>
                      <CloneIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {!agent.isSystem && (
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(agent)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <DataTable
          columns={tableColumns}
          rows={agents}
          loading={false}
          emptyMessage="No agents found"
        />
      )}
    </PageListLayout>
  );
};
